/* verqo — /preview/buyers-off-network · preview for the "Use verqo without your suppliers" section.
   Two killer off-network buyer tools: PO Generator + Inbox, and Invoice Financing.
   Goal: deliver a sign-off-ready mock that sits between Two paths and How it works on /buyers.
*/
import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, ArrowDown, FileText, Inbox, Truck, CheckCircle2, CircleDot,
  Send, ScanLine, Banknote, CalendarClock, Building2, ShieldCheck, Sparkles,
} from "lucide-react";
import { VerqoLockup } from "../../components/VerqoLockup";

const Eyebrow = ({ children }) => (
  <div className="inline-flex items-center gap-2 text-[10.5px] uppercase tracking-[0.28em] text-lime-700 font-semibold">
    <CircleDot className="w-2.5 h-2.5 fill-lime-500 text-lime-500" />
    {children}
  </div>
);

const Reveal = ({ children, delay = 0, y = 24 }) => (
  <motion.div
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
  >
    {children}
  </motion.div>
);

/* ────────────────────────────────────────────────────────────────────── */
/* Mock 1 — PO Generator → Off-network supplier → Inbox                   */
/* ────────────────────────────────────────────────────────────────────── */
function POGeneratorMock() {
  return (
    <div className="relative rounded-2xl overflow-hidden bg-[#0c1612] p-6 md:p-7 h-[340px]">
      <div className="absolute inset-0 opacity-[0.05] pointer-events-none"
        style={{ backgroundImage: "linear-gradient(rgba(220,255,58,1) 1px, transparent 1px), linear-gradient(90deg, rgba(220,255,58,1) 1px, transparent 1px)", backgroundSize: "26px 26px" }}
      />
      <div className="absolute inset-0 opacity-30 pointer-events-none"
        style={{ background: "radial-gradient(circle at 50% 35%, rgba(220,255,58,0.16) 0%, transparent 60%)" }}
      />
      <div className="relative h-full grid grid-cols-3 items-center gap-3">
        {/* You draft PO */}
        <div className="flex flex-col items-center text-center">
          <div className="w-full max-w-[150px] rounded-lg bg-white text-slate-900 p-3 shadow-[0_18px_36px_-18px_rgba(0,0,0,0.6)]">
            <div className="text-[8px] uppercase tracking-[0.22em] text-slate-400 mb-1">PO #1042</div>
            <div className="h-1.5 rounded bg-slate-200 mb-1.5" />
            <div className="h-1.5 rounded bg-slate-200 mb-1.5 w-3/4" />
            <div className="mt-2 grid grid-cols-2 gap-1.5">
              <div className="rounded bg-lime-300 px-1.5 py-0.5 text-[8px] font-semibold text-slate-900 text-center">Net-60</div>
              <div className="rounded bg-slate-100 px-1.5 py-0.5 text-[8px] text-slate-600 text-center">$24,800</div>
            </div>
            <div className="mt-2 h-1 rounded bg-slate-200" />
            <div className="mt-1 h-1 rounded bg-slate-200 w-2/3" />
            <div className="mt-2.5 rounded bg-[#0c1612] text-white text-[8.5px] py-1 text-center font-semibold">Send PO</div>
          </div>
          <div className="mt-2.5 text-[10px] uppercase tracking-[0.18em] text-lime-400 font-semibold">You draft</div>
          <div className="text-[9.5px] text-white/55 mt-0.5">terms baked in</div>
        </div>

        {/* Arrow → Supplier */}
        <div className="flex flex-col items-center text-center px-2">
          <motion.div
            initial={{ x: -10, opacity: 0.3 }}
            animate={{ x: [-10, 10, -10], opacity: [0.3, 1, 0.3] }}
            transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
            className="flex items-center gap-1"
          >
            <Send className="w-3.5 h-3.5 text-lime-300" />
            <div className="h-px w-10 bg-gradient-to-r from-lime-300/80 to-lime-300/30" />
            <ArrowRight className="w-3 h-3 text-lime-300" />
          </motion.div>
          <div className="mt-3 w-full max-w-[130px] rounded-lg border border-white/15 bg-white/[0.04] p-2.5 backdrop-blur-sm">
            <div className="flex items-center gap-1.5 mb-1.5">
              <Building2 className="w-3 h-3 text-lime-300" />
              <div className="text-[8.5px] text-white/70 uppercase tracking-wider">Off-network</div>
            </div>
            <div className="text-[10px] font-semibold text-white leading-tight">Acme Mfg Co.</div>
            <div className="text-[8.5px] text-white/50 mt-0.5">Accepts → uploads invoice + tracking #</div>
          </div>
          <div className="mt-2 text-[9.5px] uppercase tracking-[0.18em] text-white/45 font-semibold">Supplier acts</div>
        </div>

        {/* Inbox aggregates */}
        <div className="flex flex-col items-center text-center">
          <div className="w-full max-w-[150px] rounded-lg bg-white text-slate-900 p-2.5 shadow-[0_18px_36px_-18px_rgba(0,0,0,0.6)]">
            <div className="flex items-center justify-between mb-1.5">
              <div className="flex items-center gap-1">
                <Inbox className="w-3 h-3 text-slate-700" />
                <div className="text-[8.5px] uppercase tracking-[0.18em] text-slate-500 font-semibold">Your inbox</div>
              </div>
              <div className="text-[8px] font-semibold text-lime-700">3 new</div>
            </div>
            {[
              { l: "Inv #INV-77", r: "$24.8K · Net-60" },
              { l: "Tracking added", r: "UPS · 1Z..." },
              { l: "Acme accepted", r: "PO #1042" },
            ].map((row, i) => (
              <div key={i} className="flex items-center justify-between py-1 border-t border-slate-100 first:border-t-0">
                <div className="flex items-center gap-1">
                  <span className={`w-1 h-1 rounded-full ${i === 0 ? "bg-lime-500" : "bg-slate-300"}`} />
                  <span className="text-[8.5px] text-slate-700 font-medium">{row.l}</span>
                </div>
                <span className="text-[8px] text-slate-400 tabular-nums">{row.r}</span>
              </div>
            ))}
          </div>
          <div className="mt-2.5 text-[10px] uppercase tracking-[0.18em] text-lime-400 font-semibold">Everything in one inbox</div>
          <div className="text-[9.5px] text-white/55 mt-0.5">no email chasing</div>
        </div>
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────── */
/* Mock 2 — Invoice Financing flow                                        */
/* ────────────────────────────────────────────────────────────────────── */
function InvoiceFinancingMock() {
  return (
    <div className="relative rounded-2xl overflow-hidden bg-[#0c1612] p-6 md:p-7 h-[340px]">
      <div className="absolute inset-0 opacity-[0.05] pointer-events-none"
        style={{ backgroundImage: "linear-gradient(rgba(220,255,58,1) 1px, transparent 1px), linear-gradient(90deg, rgba(220,255,58,1) 1px, transparent 1px)", backgroundSize: "26px 26px" }}
      />
      <div className="absolute inset-0 opacity-30 pointer-events-none"
        style={{ background: "radial-gradient(circle at 50% 35%, rgba(220,255,58,0.16) 0%, transparent 60%)" }}
      />
      <div className="relative h-full flex flex-col">
        {/* Top row — Invoice scan with instant approval stamp */}
        <div className="grid grid-cols-2 gap-4 items-center">
          <div className="relative">
            <div className="rounded-lg bg-white text-slate-900 p-3 shadow-[0_18px_36px_-18px_rgba(0,0,0,0.6)]">
              <div className="flex items-center gap-1.5 mb-2">
                <ScanLine className="w-3 h-3 text-slate-700" />
                <div className="text-[8.5px] uppercase tracking-[0.18em] text-slate-500 font-semibold">Invoice from supplier</div>
              </div>
              <div className="h-1.5 rounded bg-slate-200 mb-1" />
              <div className="h-1.5 rounded bg-slate-200 mb-1 w-3/4" />
              <div className="h-1.5 rounded bg-slate-200 w-1/2" />
              <div className="mt-2 flex items-center justify-between">
                <span className="text-[9px] text-slate-500">Due in 60d</span>
                <span className="text-[12px] font-bold text-slate-900 tabular-nums">$24,800</span>
              </div>
            </div>
            <motion.div
              initial={{ rotate: -8, scale: 0.85, opacity: 0 }}
              whileInView={{ rotate: -12, scale: 1, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.6, duration: 0.5, type: "spring", stiffness: 220 }}
              className="absolute -top-1.5 -right-2 px-2.5 py-1 rounded-md bg-lime-300 border-2 border-lime-500 text-[9px] font-bold uppercase tracking-wider text-slate-900 shadow-[0_8px_18px_-8px_rgba(132,204,22,0.7)]"
            >
              Approved · &lt; 60s
            </motion.div>
          </div>
          <div className="flex flex-col items-start text-left">
            <div className="text-[10px] uppercase tracking-[0.18em] text-lime-400 font-semibold mb-1.5">Snap or upload</div>
            <div className="text-[12.5px] text-white leading-snug">
              Any invoice. <span className="text-white/55">From any supplier — on or off verqo.</span>
            </div>
            <div className="mt-2 inline-flex items-center gap-1 text-[10px] text-white/55">
              <Sparkles className="w-2.5 h-2.5 text-lime-300" />
              AI underwriter approves
            </div>
          </div>
        </div>

        {/* Bottom row — money flow timeline */}
        <div className="mt-auto pt-4">
          <div className="grid grid-cols-3 gap-2 items-center">
            {/* Lender pays supplier */}
            <div className="rounded-lg border border-white/15 bg-white/[0.04] p-2.5">
              <div className="flex items-center gap-1.5 mb-1">
                <Banknote className="w-3 h-3 text-lime-300" />
                <span className="text-[8.5px] uppercase tracking-wider text-white/55 font-semibold">Day 0</span>
              </div>
              <div className="text-[10px] text-white leading-tight font-semibold">Lender pays supplier</div>
              <div className="text-[8.5px] text-white/45 mt-0.5">in 24h, direct</div>
            </div>
            {/* Animated dashed connector */}
            <div className="flex items-center justify-center px-1">
              <div className="relative w-full h-px">
                <motion.div
                  className="absolute inset-y-0 left-0 h-px bg-gradient-to-r from-lime-300 to-lime-300/0"
                  initial={{ width: "0%" }}
                  whileInView={{ width: "100%" }}
                  viewport={{ once: true }}
                  transition={{ delay: 1.2, duration: 1.4, ease: "easeInOut" }}
                />
                <div className="absolute inset-y-0 left-0 right-0 border-t border-dashed border-white/15" />
              </div>
            </div>
            {/* You pay on due date */}
            <div className="rounded-lg border border-lime-400/40 bg-lime-300/10 p-2.5">
              <div className="flex items-center gap-1.5 mb-1">
                <CalendarClock className="w-3 h-3 text-lime-300" />
                <span className="text-[8.5px] uppercase tracking-wider text-lime-200 font-semibold">Day 60</span>
              </div>
              <div className="text-[10px] text-white leading-tight font-semibold">You repay lender</div>
              <div className="text-[8.5px] text-white/55 mt-0.5">on your due date</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────── */
/* The actual section being previewed                                      */
/* ────────────────────────────────────────────────────────────────────── */
export function BuyerOffNetworkSection() {
  return (
    <section id="tools" className="relative py-10 md:py-14 px-6 md:px-12 max-w-[1280px] mx-auto" data-testid="buyers-off-network-section">
      <Reveal>
        <div className="text-center max-w-3xl mx-auto">
          <Eyebrow>Works without your suppliers</Eyebrow>
          <h2 className="mt-5 text-3xl md:text-5xl font-semibold tracking-tight leading-[1.02] text-slate-900">
            Two tools. <span className="text-slate-400">Zero supplier signup.</span>
          </h2>
          <p className="mt-4 text-[15px] text-slate-600 max-w-2xl mx-auto leading-relaxed">
            You don't have to wait for your suppliers to join verqo. Run your wholesale on our hub today — and get financed on every invoice they send you.
          </p>
        </div>
      </Reveal>

      <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-5 md:gap-6">
        {/* ─── Card 1 · PO Generator + Inbox ─── */}
        <Reveal delay={0.06}>
          <div className="h-full rounded-2xl border border-slate-200 bg-white p-7 md:p-8 hover:border-lime-300 transition-all flex flex-col" data-testid="buyers-tool-po-generator">
            <div className="flex items-center justify-between mb-5">
              <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-lime-100 text-lime-800 text-[10px] uppercase tracking-[0.18em] font-semibold">
                <CircleDot className="w-2 h-2 fill-lime-600 text-lime-600" />
                Tool 1 · Off-network ready
              </div>
              <div className="inline-flex items-center gap-1.5 text-[10.5px] text-slate-500">
                <FileText className="w-3.5 h-3.5" /> + <Inbox className="w-3.5 h-3.5" />
              </div>
            </div>

            <h3 className="text-xl md:text-2xl font-semibold text-slate-900 leading-tight tracking-tight">
              PO Generator &amp; Inbox.
            </h3>
            <p className="mt-2.5 text-[13.5px] text-slate-600 leading-relaxed">
              Draft a PO with the terms <em>you</em> want, send it to any supplier — on or off verqo — and watch their acceptance, invoice, and tracking thread back into one inbox.
            </p>

            <div className="mt-5">
              <POGeneratorMock />
            </div>

            <ul className="mt-5 space-y-2.5 flex-1">
              {[
                { t: "Draft & send", d: "You set the SKUs, qty, ship-by, and Net-30/60/90 terms. One click sends a secure PO link to your supplier." },
                { t: "Supplier accepts off-network", d: "No verqo account needed. They click your link, accept, upload the invoice, and add tracking — straight from email." },
                { t: "Everything threads to your inbox", d: "Acceptances, invoices, ship updates, doc changes — every supplier in one place. No more chasing email threads." },
              ].map((s, i) => (
                <li key={s.t} className="flex items-start gap-3" data-testid={`po-step-${i + 1}`}>
                  <span className="mono text-[10px] text-slate-400 tracking-[0.15em] mt-1 flex-shrink-0 tabular-nums">{String(i + 1).padStart(2, "0")}</span>
                  <div>
                    <div className="text-[13px] font-semibold text-slate-900 leading-snug">{s.t}</div>
                    <p className="mt-0.5 text-[12.5px] text-slate-600 leading-relaxed">{s.d}</p>
                  </div>
                </li>
              ))}
            </ul>

            <a href="/register?as=buyer" data-testid="po-cta" className="group mt-6 inline-flex items-center gap-1.5 text-[13px] font-semibold text-slate-900 hover:text-lime-700 transition-colors">
              Start generating POs
              <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
            </a>
          </div>
        </Reveal>

        {/* ─── Card 2 · Invoice Financing ─── */}
        <Reveal delay={0.14}>
          <div className="h-full rounded-2xl border border-slate-200 bg-white p-7 md:p-8 hover:border-lime-300 transition-all flex flex-col" data-testid="buyers-tool-invoice-financing">
            <div className="flex items-center justify-between mb-5">
              <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-lime-100 text-lime-800 text-[10px] uppercase tracking-[0.18em] font-semibold">
                <CircleDot className="w-2 h-2 fill-lime-600 text-lime-600" />
                Tool 2 · No supplier signup
              </div>
              <div className="inline-flex items-center gap-1.5 text-[10.5px] text-slate-500">
                <Banknote className="w-3.5 h-3.5" />
              </div>
            </div>

            <h3 className="text-xl md:text-2xl font-semibold text-slate-900 leading-tight tracking-tight">
              Invoice Financing.
            </h3>
            <p className="mt-2.5 text-[13.5px] text-slate-600 leading-relaxed">
              Snap any invoice from any supplier. Get approved in seconds. Our lender pays your supplier direct — you settle on the original due date.
            </p>

            <div className="mt-5">
              <InvoiceFinancingMock />
            </div>

            <ul className="mt-5 space-y-2.5 flex-1">
              {[
                { t: "Snap or upload an invoice", d: "Any invoice. From any supplier — even off-network. PDF, photo, or email forward." },
                { t: "Instant approval", d: "Our AI underwriter uses your credit line + invoice details to approve in under 60 seconds. No paperwork, no calls." },
                { t: "Lender pays supplier · you pay later", d: "Your supplier gets paid within 24h, direct. You repay the lender on the invoice's original due date (Net-30/60/90)." },
              ].map((s, i) => (
                <li key={s.t} className="flex items-start gap-3" data-testid={`fin-step-${i + 1}`}>
                  <span className="mono text-[10px] text-slate-400 tracking-[0.15em] mt-1 flex-shrink-0 tabular-nums">{String(i + 1).padStart(2, "0")}</span>
                  <div>
                    <div className="text-[13px] font-semibold text-slate-900 leading-snug">{s.t}</div>
                    <p className="mt-0.5 text-[12.5px] text-slate-600 leading-relaxed">{s.d}</p>
                  </div>
                </li>
              ))}
            </ul>

            <a href="/register?as=buyer" data-testid="fin-cta" className="group mt-6 inline-flex items-center gap-1.5 text-[13px] font-semibold text-slate-900 hover:text-lime-700 transition-colors">
              Finance your first invoice
              <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
            </a>
          </div>
        </Reveal>
      </div>

      {/* Bottom strip — reinforces the "your suppliers don't even need to know" angle */}
      <Reveal delay={0.22}>
        <div className="mt-8 md:mt-10 rounded-2xl border border-slate-200 bg-slate-50/60 px-6 md:px-8 py-5 md:py-6 flex flex-wrap items-center justify-between gap-4" data-testid="buyers-off-network-strip">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-[#0c1612] grid place-items-center">
              <ShieldCheck className="w-4 h-4 text-lime-300" />
            </div>
            <div>
              <div className="text-[13px] font-semibold text-slate-900">Your suppliers don't even need to know verqo exists.</div>
              <div className="text-[12px] text-slate-600 mt-0.5">Both tools work today, on your existing supplier base. No onboarding emails. No friction. Pure upside.</div>
            </div>
          </div>
          <a href="/register?as=buyer" data-testid="off-network-strip-cta" className="text-[12.5px] font-semibold text-slate-900 inline-flex items-center gap-1.5 hover:text-lime-700 transition-colors">
            Get pre-approved — free
            <ArrowRight className="w-3.5 h-3.5" />
          </a>
        </div>
      </Reveal>
    </section>
  );
}

/* ────────────────────────────────────────────────────────────────────── */
/* The full preview page wrapper                                           */
/* ────────────────────────────────────────────────────────────────────── */
export default function BuyersOffNetworkPreview() {
  useEffect(() => { document.title = "verqo · Off-network tools preview"; }, []);

  return (
    <div className="min-h-screen text-slate-900 antialiased selection:bg-lime-300" style={{ background: "#FAFAF7" }}>
      {/* Floating nav (matches /buyers chrome) */}
      <header className="fixed top-0 inset-x-0 z-40 backdrop-blur-md bg-white/80 border-b border-slate-200/60">
        <div className="max-w-[1280px] mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
          <Link to="/buyers" className="inline-flex items-center group">
            <VerqoLockup />
            <span className="ml-1 text-[11px] text-slate-500 hidden md:inline">· Off-network tools preview</span>
          </Link>
          <Link to="/buyers" className="text-[12px] text-slate-600 hover:text-slate-900 transition-colors">
            ← Back to /buyers
          </Link>
        </div>
      </header>

      {/* Top context — shows what comes before */}
      <section className="pt-28 md:pt-32 pb-8 md:pb-10 px-6 md:px-12 max-w-[1280px] mx-auto">
        <div className="rounded-xl bg-slate-100/70 border border-slate-200/60 px-5 py-4 text-[12px] text-slate-600 flex items-center gap-3" data-testid="preview-context">
          <span className="text-[10px] uppercase tracking-[0.22em] text-slate-400 font-semibold">Sits after</span>
          <span className="font-semibold text-slate-900">Two paths · Either way, you win</span>
          <ArrowDown className="w-3 h-3 text-slate-400" />
          <span className="text-slate-500">Below is the new section</span>
        </div>
      </section>

      {/* The actual section */}
      <BuyerOffNetworkSection />

      {/* Bottom context — shows what comes after */}
      <section className="py-8 md:py-10 px-6 md:px-12 max-w-[1280px] mx-auto">
        <div className="rounded-xl bg-slate-100/70 border border-slate-200/60 px-5 py-4 text-[12px] text-slate-600 flex items-center gap-3" data-testid="preview-after">
          <span className="text-[10px] uppercase tracking-[0.22em] text-slate-400 font-semibold">Then</span>
          <ArrowDown className="w-3 h-3 text-slate-400" />
          <span className="font-semibold text-slate-900">How it works · 60 seconds to approved</span>
        </div>

        <div className="mt-12 flex items-center justify-center gap-4">
          <Link to="/buyers" className="px-6 py-3 rounded-lg border border-slate-300 text-slate-900 text-sm hover:bg-slate-50 transition-colors" data-testid="preview-back-cta">
            ← Back to live /buyers
          </Link>
          <a href="#" onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: "smooth" }); }} className="px-6 py-3 rounded-lg bg-[#0c1612] text-white text-sm font-semibold hover:opacity-90 transition-opacity" data-testid="preview-top-cta">
            Re-read from top ↑
          </a>
        </div>
      </section>

      <footer className="px-6 md:px-12 max-w-[1280px] mx-auto py-10 border-t border-slate-200 flex items-center justify-between text-[11px] text-slate-500">
        <span>preview · /preview/buyers-off-network</span>
        <span>verqo · Wholesale, instant.</span>
      </footer>
    </div>
  );
}
